import { useCallback, useEffect, useState } from "react";
import { ReactComponent as CheckIcon } from "../../Assets/check-solid.svg";
import { useFormStore } from "../../Stores/Form";
import { activateOnKey } from "../../Helper/a11y";
import "./MultipleChoiceFormItem.scss";

function shuffleArray(array: any[]) {
  for (var i = array.length - 1; i > 0; i--) {
    var j = Math.floor(Math.random() * (i + 1));
    var temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }
}

function MultipleChoiceFormItem({
  formItem,
  prioritySelect,
}: {
  formItem: any;
  prioritySelect?: boolean;
}) {
  const formData = useFormStore((state) => state.formData);
  const [options, setOption] = useState([] as any[]);
  const setFormData = useFormStore((state) => state.setFormData);
  const formKey = prioritySelect ? "priority" : formItem?.name;
  const fieldFormData = formData[formKey];

  useEffect(() => {
    var options = (formItem?.choices || []).map((choice: any) => {
      if (choice && choice.id && !choice.value) {
        return {
          ...choice,
          value: choice.id,
        };
      }
      return choice;
    });
    if (options) {
      if (formItem?.randomizeChoices) {
        shuffleArray(options);
      }

      setOption(options);
    }
  }, [formItem?.choices]);

  const getIndex = (character: string) => {
    return character.charCodeAt(0) - 97;
  };

  const getCharacter = (index: number) => {
    return String.fromCharCode(97 + index);
  };

  const handleUserKeyPress = useCallback((event: any) => {
    const { key, target } = event;
    if (key && target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") {
      const index = getIndex(key);
      if (index >= 0 && index < options.length) {
        setFormData(
          formKey,
          {
            value: options[index]?.value,
            dirty: true,
          },
          true
        );
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleUserKeyPress);
    return () => {
      window.removeEventListener("keydown", handleUserKeyPress);
    };
  }, [handleUserKeyPress]);

  const getLabel = (option: any) => {
    if (option && option.label) {
      return option.label;
    }
    if (typeof option === "object") {
      return option[""];
    }
    return option;
  };

  // Board priorities carry their own color; older forms fall back to the
  // index-based low/medium/high colors.
  const renderPriorityBubble = (option: any, index: number) => {
    const color =
      typeof option?.color === "string" && option.color ? option.color : null;
    if (color) {
      return (
        <div
          className="multiple-choice-form-item-choice-priority"
          style={{ backgroundColor: color }}
        />
      );
    }
    return (
      <div
        className={`multiple-choice-form-item-choice-priority multiple-choice-form-item-choice-priority--${index}`}
      />
    );
  };

  const renderOption = (option: any, index: number) => {
    const isSelected = fieldFormData && fieldFormData.value === option?.value;
    const onItemClick = () => {
      setFormData(
        formKey,
        {
          value: option?.value,
          dirty: true,
        },
        true
      );
    };
    return (
      <div
        key={index}
        className={`multiple-choice-form-item-choice ${
          isSelected &&
          "multiple-choice-form-item-choice--selected"
        }`}
        role="radio"
        aria-checked={isSelected}
        tabIndex={0}
        onClick={onItemClick}
        onKeyDown={activateOnKey(onItemClick)}
      >
        {prioritySelect ? (
          renderPriorityBubble(option, index)
        ) : (
          <div className="multiple-choice-form-item-choice-shortcut">
            {getCharacter(index)}
          </div>
        )}
        <div className="multiple-choice-form-item-choice-value">
          {getLabel(option)}
        </div>
        <div className="multiple-choice-form-item-choice-check">
          <CheckIcon />
        </div>
      </div>
    );
  };

  return (
    <div id={formItem.name} className="multiple-choice-form-item" role="radiogroup" aria-required={formItem.required}>
      {options.map((choice: any, index: number) => renderOption(choice, index))}
    </div>
  );
}

export default MultipleChoiceFormItem;
